import { useApi } from "@/hooks";
import { DSIcon } from "@Components/common/byb-ds/icons";
import BybVrDialog from "@Components/game/common/dialog/byb-vr-dialog";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import AnalyticsEvents from "@Enums/AnalyticsEvents";
import MetricEventTypes from "@Enums/MetricEventTypes";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { useGlobalContext } from "@hooks/index";
import useI18n from "@i18n";
import { Box, DialogContent, DialogTitle, IconButton, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { GuideRecord } from "@Shared/schemas/guides/guideSchema";
import { metricGuides } from "@shared/utilities/guidesConfig";
import { useEffect, useState } from "react";

const VIDEO_THUMBNAIL = "https://challenge-thumbs.b-cdn.net/byb-backgrounds/activity_bg.png";

interface MetricDetailsDialogProps {
  isTrainingFocus: boolean;
  metric: MetricEventTypes | null;
  open: boolean;
  onClose: () => void;
}

const MetricDetailsDialog = ({ isTrainingFocus, metric, open, onClose }: MetricDetailsDialogProps) => {
  const { globalState } = useGlobalContext();
  const { track } = useAnalytics();
  const theme = useTheme();
  const { i18n } = useI18n();
  const { call: getGuides, loading } = useApi<typeof Api.Guides.GetGuides>({
    api: Api.Guides.GetGuides,
    disableLoader: true,
  });
  const [guide, setGuide] = useState<GuideRecord>();

  const videoGuide = metric ? metricGuides[metric] : null;

  const showVideo = (videoId?: string | null) => {
    if (!videoId || !metric) return;

    globalState.app.events.showVideo.emit({
      videoId,
    });
    track(AnalyticsEvents.metric_details_video_played, {
      metric,
      is_training_focus: isTrainingFocus,
    });
  };

  useEffect(() => {
    if (open && videoGuide) {
      getGuides({ ids: [videoGuide.id] })
        .then(response => {
          setGuide(response.data[0]);
        })
        .catch(() => undefined);
    }
  }, [getGuides, open, videoGuide?.id]);

  if (!metric) return null;

  return (
    <BybVrDialog
      name="metric-details-dialog"
      open={open}
      onClose={onClose}
      showCloseButton
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: theme.spacing(100),
        },
      }}
    >
      <DialogTitle textAlign="center">
        <Typography variant="text4xl800" color="text.primary">
          {i18n(`metric.${metric}.name`)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack gap={4}>
          <Typography variant="subtitle1" color="text.secondary" textAlign="center">
            {i18n(`metrics.details.${metric}.caption`)}
          </Typography>

          <Stack gap={4}>
            <Box width="100%" sx={{ position: "relative", height: theme.spacing(32.5) }}>
              {loading || !guide ? (
                <Skeleton variant="rounded" width="100%" height="100%" />
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${VIDEO_THUMBNAIL}) no-repeat center center`,
                    backgroundSize: "cover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: theme.spacing(1.5),
                  }}
                >
                  <HapticsButtonWrapper>
                    <IconButton onClick={() => showVideo(guide.video_id)} sx={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}>
                      <Icon dsIcon={DSIcon.PLAY} size={48} color="#FFF" />
                    </IconButton>
                  </HapticsButtonWrapper>
                </Box>
              )}
            </Box>

            <Stack gap={1}>
              <Typography variant="textLg800" color="text.primary">
                {i18n("metrics.details.why-it-matters")}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {i18n(`metrics.details.${metric}.explanation`)}
              </Typography>
            </Stack>
            <Stack gap={1}>
              <Typography variant="textLg800" color="text.primary">
                {i18n("metrics.details.how-to-train")}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {i18n(`metrics.details.${metric}.training`)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </BybVrDialog>
  );
};

export default MetricDetailsDialog;
